import React, { createContext, useState, useCallback, ReactNode } from "react";

export interface AiAssistantConfig {
  pageName: string;
  filterParams: Record<string, any>;
  isVisible: boolean;
}

export interface AiInsightConfig {
  cardKey: string;
  filterParams: Record<string, any>;
}

interface AiAssistantContextType {
  config: AiAssistantConfig | null;
  setConfig: (config: AiAssistantConfig | null) => void;
  insights: AiInsightConfig[];
  registerInsight: (cardKey: string, filterParams: Record<string, any>) => void;
  unregisterInsight: (cardKey: string) => void;
}

const AiAssistantContext = createContext<AiAssistantContextType | undefined>(undefined);

export const AiAssistantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfigState] = useState<AiAssistantConfig | null>(null);
  const [insights, setInsights] = useState<AiInsightConfig[]>([]);

  const setConfig = useCallback((cfg: AiAssistantConfig | null) => {
    setConfigState(cfg);
    if (cfg === null) {
      setInsights([]);
    }
  }, []);

  const registerInsight = useCallback((cardKey: string, filterParams: Record<string, any>) => {
    setInsights(prev => {
      const idx = prev.findIndex(i => i.cardKey === cardKey);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { cardKey, filterParams };
        return next;
      }
      return [...prev, { cardKey, filterParams }];
    });
  }, []);

  const unregisterInsight = useCallback((cardKey: string) => {
    setInsights(prev => prev.filter(i => i.cardKey !== cardKey));
  }, []);

  return (
    <AiAssistantContext.Provider value={{ config, setConfig, insights, registerInsight, unregisterInsight }}>
      {children}
    </AiAssistantContext.Provider>
  );
};

export default AiAssistantContext;
